import { Component, Input } from "@angular/core";
import { AlertController, ModalController } from "ionic-angular";
import { PatientBenefitsService } from "providers/patient-charts/patient-benefits.service";
import { UtilityService } from "providers/utility-service/utility-service";

@Component({
  selector: "patient-eligibility-row",
  templateUrl: "patient-eligibility-row.html",
})
export class PatientEligibilityRowComponent {
  @Input() eligibility: any;
  @Input() i: number;
  eligibilityReport: any;

  // CO-3399 (A.5.3 / A.5.6): rendering the three Medi-Cal fields a 271 may carry.
  //
  // THE WHOLE DIFFICULTY IS THAT "ABSENT" AND "ZERO" ARE DIFFERENT ANSWERS, and this page
  // already has a habit of erasing that: formatPatientBenefitsResponse() on the parent
  // rewrites every null and "" to "-". Run the payer's answer through that and a Share of
  // Cost the payer never mentioned becomes indistinguishable from one they reported as
  // zero - and those mean opposite things to a biller. A null routes to manual entry
  // (A.5.6, the fallback that shipped under register item #5); a zero is the payer stating
  // the member owes nothing, which is claim-affecting.
  //
  // So these presenters keep the two apart all the way to the screen, and return null only
  // for the genuinely-absent case, which the template renders as an explicit
  // "not returned by payer" rather than as a dash that could mean anything.
  //
  // Static and pure so they can be tested without a TestBed - the same shape
  // PatientBenefitsFormComponent.dollarsToCents uses for the entry side of the same field.

  /**
   * The Aid Code the payer returned, or null when they returned none.
   *
   * An empty or whitespace-only value counts as ABSENT rather than as a blank answer: the
   * backend already collapses those (firstStringOf in medi_cal_waystar_transport.go), and a
   * blank rendered as a value would read as the payer stating the member has no Aid Code.
   */
  static formatAidCode(value: any): string {
    if (value === null || value === undefined) {
      return null;
    }
    const text = String(value).trim();
    return text === "" ? null : text;
  }

  /**
   * The monthly Share of Cost, formatted from integer CENTS.
   *
   * Cents, not dollars, because that is how it is stored - money.Money on the backend, and
   * what PatientBenefitsFormComponent.dollarsToCents writes on the entry side. Formatting
   * the raw number as dollars would show $50000 for a $500.00 Share of Cost.
   *
   * A reported ZERO returns "$0.00", never null. That is the payer saying the member owes
   * nothing, and it is the difference between a claim that carries Value Code 23 and one
   * that does not.
   */
  static formatShareOfCost(cents: any): string {
    if (cents === null || cents === undefined || cents === "") {
      return null;
    }
    const amount = Number(cents);
    if (isNaN(amount)) {
      return null;
    }
    const negative = amount < 0;
    const whole = Math.floor(Math.abs(amount) / 100);
    const remainder = Math.abs(amount) % 100;
    const padded = remainder < 10 ? "0" + remainder : String(remainder);
    return (negative ? "-$" : "$") + whole + "." + padded;
  }

  /** The managed-care plan the member is enrolled in, or null when the payer returned none. */
  static formatMcpEnrollment(value: any): string {
    return PatientEligibilityRowComponent.formatAidCode(value);
  }

  // Instance wrappers, so the template can bind without reaching for the class.
  aidCode(): string {
    return PatientEligibilityRowComponent.formatAidCode(
      this.eligibility && this.eligibility.aidCode
    );
  }

  shareOfCost(): string {
    return PatientEligibilityRowComponent.formatShareOfCost(
      this.eligibility && this.eligibility.shareOfCostCents
    );
  }

  mcpEnrollment(): string {
    return PatientEligibilityRowComponent.formatMcpEnrollment(
      this.eligibility && this.eligibility.mcpEnrollment
    );
  }

  /**
   * The differences between what the payer returned and what the benefit record holds for
   * this response's own service month (A.5.4).
   *
   * SURFACED ONLY. Nothing here applies anything: entry lives on the Payers tab, per
   * benefit, and that placement was ruled on in Round-2 register item #3 - the Eligibility
   * tab is patient-level, carries no benefit context, and sits behind a different tenant
   * toggle. A biller reads the disagreement here and makes the change there.
   */
  mediCalDiffs(): any[] {
    const diffs = this.eligibility && (this.eligibility as any).diffs;
    return Array.isArray(diffs) ? diffs : [];
  }

  hasMediCalDiffs(): boolean {
    return this.mediCalDiffs().length > 0;
  }

  /**
   * Whether the differences could be computed at all.
   *
   * An unreadable benefit record and a record in perfect agreement both produce zero
   * differences, and they are opposite facts. This is what lets the screen say "no
   * differences" only when that is actually true.
   */
  diffsUnavailable(): boolean {
    return (
      this.hasMediCalDetail() &&
      this.eligibility &&
      (this.eligibility as any).diffsComputed === false
    );
  }

  /** A human label for a diff's field name. */
  static diffFieldLabel(fieldName: string): string {
    switch (fieldName) {
      case "aidCode":
        return "mediCalAidCode";
      case "shareOfCost":
        return "mediCalShareOfCost";
      case "mcpEnrollment":
        return "mediCalManagedCarePlan";
      default:
        return fieldName;
    }
  }

  diffLabel(fieldName: string): string {
    return PatientEligibilityRowComponent.diffFieldLabel(fieldName);
  }

  /**
   * Whether this row has any Medi-Cal detail to show at all.
   *
   * False for a Medicare or commercial check, so the columns stay off those rows rather
   * than filling the grid with three "not returned" cells on every payer that was never
   * asked about Aid Code.
   */
  hasMediCalDetail(): boolean {
    return (
      this.aidCode() !== null ||
      this.shareOfCost() !== null ||
      this.mcpEnrollment() !== null
    );
  }

  constructor(
    public patientBenefitService: PatientBenefitsService,
    private alertCtrl: AlertController,
    private utilService: UtilityService,
    protected modalController: ModalController
  ) {}

  ngOnInit() {}

  openInstantEligibilityReport() {
    let closeMonthModal = this.modalController.create(
      "InstantEligibilityReportModal",
      {
        eligibilityJson: this.eligibilityReport,
        ranDate: this.eligibility.dateRun,
        inquiryId: this.extractInquiryId(this.eligibility.rawOutput),
      },
      { enableBackdropDismiss: true, cssClass: "instant-eligibility-report" }
    );

    closeMonthModal.onDidDismiss((data) => {
      if (data && data.dismiss) {
      }
    });

    closeMonthModal.present();
  }

  getEligibility() {
    this.utilService.increaseSpinnerCount("Getting Eligibility Report...");
    this.patientBenefitService
      .getInstantEligibilityReport(
        this.eligibility.patientChartId,
        this.eligibility.payerId,
        this.eligibility.benefitType,
        this.eligibility.dateRun,
        this.eligibility.dataType != "INSTANT-ELIGIBILITY"
      )
      .subscribe((data) => {
        if (data) {
          let failReason = this.getFailReason(data);
          if (!failReason) {
            this.eligibilityReport = data;
            this.openInstantEligibilityReport();
          } else {
            this.onFailure(failReason);
          }
          this.utilService.decreaseSpinnerCount();
        } else {
          this.utilService.decreaseSpinnerCount();
        }
      });
    this.utilService.decreaseSpinnerCount();
  }

  mapPrimacyLevelTitle(title) {
    if (title == "PRIMARY") {
      title = "Primary";
    }
    if (title == "SECONDARY") {
      title = "Secondary";
    }
    if (title == "ROOMANDBOARD") {
      title = "Room And Board";
    }
    return title;
  }

  getFailReason(json: any): string | null {
    if (json && typeof json === "object" && "FailReason" in json) {
      return json.FailReason;
    }
    if (json && typeof json === "object" && "Error" in json) {
      return json.Error.RejectReasonDescription;
    }
    return null;
  }

  onFailure(msg: string) {
    this.utilService.decreaseSpinnerCount();
    const alert = this.alertCtrl.create({
      title: "Fail Reason",
      message: msg,
      buttons: [{ text: "Cancel" }],
    });
    alert.present();
  }

  extractInquiryId(ediString: string): string | null {
    if (!ediString || ediString.length < 1) {
      return null;
    }

    const separator = ediString.charAt(3);

    const segments = ediString.split("~");

    const searchPrefix = `TRN${separator}1${separator}`;

    const trnSegment = segments.find((seg) => seg.startsWith(searchPrefix));

    if (trnSegment) {
      const elements = trnSegment.split(separator);
      if (elements.length > 2) {
        return elements[2];
      }
    }
    return null;
  }
}



// WEBPACK FOOTER //
// ./src/pages/patient-chart/patient-benefits/patient-eligibility-row/patient-eligibility-row.ts